import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import {
  AtLeast1,
  isAtLeast1,
  minItemsArrayUtils,
} from '@/modules/floorplanDesign/helpers/minItems';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { validTargetAreas, validWaitAreas } from '../constants';
import { isSingularStationArea, isStationaryArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';

export const useJitRoutingPoints = () => {
  const { showSnackbar } = useSnackbarStore();
  const { t } = useTranslation();
  const { design, getAreaById, getTemplateByAreaId, getAreasInTemplate, getJitRoutingConfigById } =
    useFloorplanDesignStore();
  const { justInTimeRoutingConfigs: allJitRoutingPoints } = design;
  const fddmSvc = FddmServiceFactory.getInstance().createFddmService();

  const isValidWaitArea = useCallback(
    async (waitAreaId: string, jitRoutingPointId: string): Promise<boolean> => {
      if (!waitAreaId || !jitRoutingPointId) return false;

      const currentJitRoutingPoint = allJitRoutingPoints.find((dp) => dp.id === jitRoutingPointId);

      if (currentJitRoutingPoint?.waitAreaIds.includes(waitAreaId)) {
        showSnackbar(t('errors:jit_routing_points.wait_area_already_targeted'));
        return false;
      }

      const waitAreaShape = getAreaById(waitAreaId);
      if (!waitAreaShape || !isStationaryArea(waitAreaShape)) return false;

      if (!validWaitAreas.includes(waitAreaShape.stationaryType)) {
        showSnackbar(
          t('errors:jit_routing_points.shape_restriction', {
            shapes: validWaitAreas.join(', '),
          }),
        );
        return false;
      }

      return true;
    },
    [allJitRoutingPoints, getAreaById, showSnackbar, t],
  );

  // TODO: incorporate manual areas when fddm supports it
  const isValidTarget = useCallback(
    (targetId: string, notifyError = true): boolean => {
      if (!targetId) return false;
      if (allJitRoutingPoints.some((dp) => dp.targetAreaIds.includes(targetId))) {
        if (notifyError) showSnackbar(t('errors:jit_routing_points.shape_already_targeted'));
        return false;
      }

      const targetShape = getAreaById(targetId);
      if (!targetShape || !isSingularStationArea(targetShape)) return false;

      if (!validTargetAreas.includes(targetShape.loadHandlingType)) {
        if (notifyError)
          showSnackbar(
            t('errors:jit_routing_points.shape_restriction', {
              shapes: validTargetAreas.join(', '),
            }),
          );
        return false;
      }

      return true;
    },
    [allJitRoutingPoints, getAreaById, showSnackbar, t],
  );

  const findSiblingAreaIds = useCallback(
    (areaId: string): string[] => {
      const template = getTemplateByAreaId(areaId);
      if (!template) return [];

      const areasInTemplate = getAreasInTemplate(template.group.id);
      if (!areasInTemplate) return [];

      return areasInTemplate.map((area) => area.id);
    },
    [getTemplateByAreaId, getAreasInTemplate],
  );

  const hasPartialGroupDeletion = useCallback(
    (targetIds: string[], newTargetIds: string[]): boolean =>
      targetIds.some((targetId) => {
        const siblingShapeIds = findSiblingAreaIds(targetId);
        if (!siblingShapeIds || siblingShapeIds.length < 2) return false;
        const presentSiblings = siblingShapeIds.filter((id) => newTargetIds.includes(id));
        return presentSiblings.length > 0 && presentSiblings.length < siblingShapeIds.length;
      }),
    [findSiblingAreaIds],
  );

  const removeFromJitRoutingPoint = useCallback(
    (shapeIdsToDelete: string[], jitRoutingPointId: string) => {
      const jitRoutingConfig = getJitRoutingConfigById(jitRoutingPointId);
      if (!jitRoutingConfig) return;

      const newTargetIds = jitRoutingConfig.targetAreaIds.filter(
        (id) => !shapeIdsToDelete.includes(id),
      );
      const newWaitAreasIds = jitRoutingConfig.waitAreaIds.filter(
        (id) => !shapeIdsToDelete.includes(id),
      );

      const isMisconfigured = hasPartialGroupDeletion(shapeIdsToDelete, newTargetIds);
      if (isMisconfigured) {
        showSnackbar(t('interface:jit_routing_points.possible_misconfiguration'));
      }

      if (
        !isAtLeast1<string>(newTargetIds) ||
        (newTargetIds.length === jitRoutingConfig.targetAreaIds.length &&
          newWaitAreasIds.length === jitRoutingConfig.waitAreaIds.length)
      ) {
        return;
      }

      fddmSvc.updateJitRoutingConfig({
        ...jitRoutingConfig,
        targetAreaIds: newTargetIds,
        waitAreaIds: newWaitAreasIds,
      });
    },
    [fddmSvc, getJitRoutingConfigById, hasPartialGroupDeletion, showSnackbar, t],
  );

  const getValidSiblingTargets = useCallback(
    (targetId: string): string[] => {
      const siblingShapeIds = findSiblingAreaIds(targetId);
      if (!siblingShapeIds || siblingShapeIds.length === 0) {
        return [];
      }

      const validationResults = siblingShapeIds.map((id) => isValidTarget(id, false));
      return siblingShapeIds.filter((_, i) => validationResults[i]);
    },
    [findSiblingAreaIds, isValidTarget],
  );

  const addWaitAreaToJitRoutingPoint = useCallback(
    async (waitAreaId: string, jitRoutingPointId: string, oldValue?: string) => {
      const isValid = await isValidWaitArea(waitAreaId, jitRoutingPointId);
      if (!isValid) {
        return;
      }

      const jitRoutingConfig = getJitRoutingConfigById(jitRoutingPointId);
      if (!jitRoutingConfig) return;

      let updatedWaitAreaIds: string[];

      if (oldValue) {
        updatedWaitAreaIds = jitRoutingConfig.waitAreaIds.map((id) =>
          id === oldValue ? waitAreaId : id,
        );
      } else {
        updatedWaitAreaIds = [...new Set([...jitRoutingConfig.waitAreaIds, waitAreaId])];
      }

      fddmSvc.updateJitRoutingConfig({
        ...jitRoutingConfig,
        waitAreaIds: updatedWaitAreaIds,
      });
    },
    [fddmSvc, getJitRoutingConfigById, isValidWaitArea],
  );

  const addTargetToJitRoutingPoint = useCallback(
    (targetId: string, jitRoutingPointId: string, oldValue?: string) => {
      const validSiblingShapeIds = getValidSiblingTargets(targetId);

      if (validSiblingShapeIds.length === 0) {
        if (!isValidTarget(targetId)) return;
      } else {
        showSnackbar(t('interface:jit_routing_points.added_all_shapes_of_group_as_target'));
      }

      const jitRoutingConfig = getJitRoutingConfigById(jitRoutingPointId);
      if (!jitRoutingConfig) return;

      let newTargetIds: AtLeast1<string>;
      const allNewTargetIds: AtLeast1<string> = [targetId, ...validSiblingShapeIds];

      if (oldValue) {
        newTargetIds = minItemsArrayUtils.mapAtLeast1(
          jitRoutingConfig.targetAreaIds,
          (targetAreaId) => (targetAreaId === oldValue ? targetId : targetAreaId),
        );
      } else {
        newTargetIds = [...jitRoutingConfig.targetAreaIds, ...allNewTargetIds];
      }

      fddmSvc.updateJitRoutingConfig({
        ...jitRoutingConfig,
        targetAreaIds: newTargetIds,
      });
    },
    [fddmSvc, getJitRoutingConfigById, getValidSiblingTargets, isValidTarget, showSnackbar, t],
  );

  const removeJitRoutingPoint = useCallback(
    (jitRoutingPointId: string) => {
      const jitRoutingConfig = getJitRoutingConfigById(jitRoutingPointId);
      if (!jitRoutingConfig) return;

      fddmSvc.deleteJitRoutingConfigs([jitRoutingConfig]);
    },
    [getJitRoutingConfigById, fddmSvc],
  );

  const setRelativePositionForJitRoutingPoint = useCallback(
    (value: number, jitRoutingPointId: string) => {
      const jitRoutingConfig = getJitRoutingConfigById(jitRoutingPointId);
      if (!jitRoutingConfig) return;
      fddmSvc.updateJitRoutingConfig({
        ...jitRoutingConfig,
        relativePositionOnHighwayInPerc: value,
      });
    },
    [fddmSvc, getJitRoutingConfigById],
  );

  return {
    removeFromJitRoutingPoint,
    addTargetToJitRoutingPoint,
    addWaitAreaToJitRoutingPoint,
    removeJitRoutingPoint,
    setRelativePositionForJitRoutingPoint,
    getValidSiblingTargets,
    isValidTarget,
  };
};
